"use client";

import { isDevelopment } from "../../utils/env";
import { DevTool } from "@hookform/devtools";
import { Stack, StackProps } from "@mui/material";
import { styled, SxProps, Theme } from "@mui/material/styles";
import { BaseSyntheticEvent, ReactNode } from "react";
import { FieldValues, FormProvider, UseFormReturn } from "react-hook-form";
import { ActionButtonsWrapper, ActionButtonsWrapperProps } from "../Buttons/ActionButtonsWrapper";

interface FormWrapperProps<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
  TTransformedValues = TFieldValues,
> {
  children: ReactNode;
  methods: UseFormReturn<TFieldValues, TContext, TTransformedValues>;
  onSubmit: (data: TTransformedValues, event?: BaseSyntheticEvent) => void | Promise<void>;
  dataTestid: string;
  spacing?: number;
  alignItems?: string;
  renderActionButtons?: React.ReactNode;
  actionButtonsProps?: ActionButtonsWrapperProps;
  id?: string;
  stackProps?: StackProps;
  sx?: SxProps<Theme>;
  [key: string]: unknown;
}

const StyledForm = styled("form")<{
  hasStickyButtonsWrapper?: boolean;
}>(({ hasStickyButtonsWrapper }) => ({
  height: hasStickyButtonsWrapper ? "100%" : undefined,
  display: "flex",
  flexDirection: "column",
  overflow: "auto",
}));

export const FormWrapper = <TFormValues extends FieldValues>({
  children,
  schema,
  methods,
  onSubmit,
  dataTestid,
  renderActionButtons,
  actionButtonsProps,
  stackProps,
  alignItems = "stretch",
  spacing = 3,
  ...props
}: FormWrapperProps<TFormValues>) => {
  const hasStickyButtonsWrapper = actionButtonsProps?.variant === "sticky";

  const handleSubmit = methods.handleSubmit((data, event) => {
    onSubmit(data, event);
    // TODO: trimming destroys date strings, temporary disabling it
    // const trimmedData = trimStringValues(data);
    // onSubmit(trimmedData);
  });

  return (
    // TS Performance improvement
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <FormProvider {...(methods as any)}>
      <StyledForm
        data-testid={dataTestid}
        onSubmit={handleSubmit}
        hasStickyButtonsWrapper={hasStickyButtonsWrapper}
        {...props}
      >
        <div style={{ flex: 1, overflow: "auto" }}>
          <Stack
            spacing={spacing}
            alignItems={alignItems}
            flexGrow={1}
            p="0.5rem 4.5rem"
            minHeight={0}
            {...stackProps}
          >
            {children}
          </Stack>
        </div>

        {renderActionButtons && (
          <ActionButtonsWrapper {...actionButtonsProps}>{renderActionButtons}</ActionButtonsWrapper>
        )}
      </StyledForm>

      {isDevelopment() && <DevTool control={methods.control} placement="top-right" />}
      {/* it breaks form layout in many places so first we need to figure out how to scale it properly */}
      {/* {isDevelopment() && <pre>{JSON.stringify(methods.formState, null, 2)}</pre>} */}
    </FormProvider>
  );
};
